import { asNullableNumber as financeNumber } from './common';
import {
  asArray,
  asNumber,
  asOptionalString,
  asRecord,
  asString,
  reportDtoAnomaly,
} from './common';
import type { OrderSettlementEntry, OrderSettlementStatus } from '../modules/orders/types';

export interface OrderSettlementDto {
  requiresUpdate?: boolean;
  invoiceBased?: boolean;
  costStatus?: 'KNOWN' | 'PENDING';
  planned: {
    revenue?: number;
    totalCost?: number;
    margin?: number;
    marginPercent?: number;
  };
  actual: {
    revenue: number;
    totalCost: number | null;
    margin: number | null;
    marginPercent: number | null;
  };
  settlementStatus: OrderSettlementStatus;
  settled: {
    revenue: number;
    totalCost: number | null;
    margin: number | null;
    marginPercent: number | null;
    settledAt?: string | null;
  } | null;
  entries: OrderSettlementEntry[];
}

/**
 * The only settlement-command outcomes visible to an operational user.
 * They deliberately reveal whether a frozen physical tranche was accounted
 * for, but never why a financial evaluation required an administrator.
 */
export type OrderSettlementCommandState = 'APPLIED' | 'ADMIN_REQUIRED' | 'SUPERSEDED';
export type OrderSettlementCommandOutcome = 'APPLIED' | 'ADMIN_REQUIRED' | 'DUPLICATE' | 'SUPERSEDED';
export type OrderSettlementCommandSubmitState = Exclude<OrderSettlementCommandState, 'SUPERSEDED'>;
export type OrderSettlementCommandSubmitOutcome = Exclude<OrderSettlementCommandOutcome, 'SUPERSEDED'>;

export interface OrderSettlementCommandResultDto {
  /**
   * Submit returns APPLIED or ADMIN_REQUIRED. An ADMIN SUPERSEDE resolution
   * additionally returns SUPERSEDED, which must remain an ADMIN-only flow.
   */
  state: OrderSettlementCommandState | null;
  outcome: OrderSettlementCommandOutcome | null;
}

/** The public one-shot submit endpoint cannot produce a SUPERSEDED result. */
export interface OrderSettlementCommandSubmitResultDto {
  state: OrderSettlementCommandSubmitState | null;
  outcome: OrderSettlementCommandSubmitOutcome | null;
}

export interface UnresolvedOrderSettlementCommandDto {
  recognitionMonth?: string;
  id: string;
  state: 'ADMIN_REQUIRED';
  physicalVersion: string;
  /** ADMIN-only diagnostic code. The backend must redact the whole object for OPERATIONS. */
  internalReasonCode: string | null;
}

export interface OrderSettlementReadinessDto {
  orderId: string;
  /** Physical readiness only — it must not be derived from finance or the ledger. */
  canSubmit: boolean;
  /** Opaque optimistic-concurrency token calculated from physical data only. */
  physicalVersion: string;
  blockedReasons: Array<{ code: string; message: string }>;
  /** A coarse result of the last frozen command, safe for OPERATIONS. */
  commandState: OrderSettlementCommandState | null;
  lines: Array<{
    orderProductId: string;
    productName: string;
    unit: string | null;
    orderedQuantity: number;
    deliveredQuantity: number;
    settlementEligibleQuantity: number;
    recognizedQuantity: number;
    readyQuantity: number;
    basisTransportIds: string[];
  }>;
  transports: Array<{ id: string; status: string; routeNumber: string; actualDelivery: string | null; cargoPresent: boolean }>;
  basisTransports: Array<{ transportId: string; routeNumber: string; status: string; actualDelivery: string | null; cargo: Array<{ orderProductId: string; unit: string | null; deliveredQuantity: number; readyQuantity: number }> }>;
  actions: { submit: boolean; finalize: boolean; finalizeWithRemainder?: boolean };
  /** ADMIN-only financial concurrency token. Never substitute it for physicalVersion. */
  financialPreviewVersion?: string;
  financialPreview?: { revenue: number; totalCost: number | null; margin: number | null; marginPercent: number };
  /** ADMIN-only reasons; never render them from an operational surface. */
  financialBlockedReasons?: Array<{ code: string; message: string }>;
  /** ADMIN-only command details. It is intentionally absent for OPERATIONS. */
  unresolvedCommand?: UnresolvedOrderSettlementCommandDto;
}

export function normalizeOrderSettlementReadiness(raw: unknown, entity = 'orders.settlementReadiness'): OrderSettlementReadinessDto {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);
  const number = (value: unknown) => asNumber(value);
  const commandState = normalizeSettlementCommandState(record?.commandState, `${entity}.commandState`);
  const rawFinancialPreview = asRecord(record?.financialPreview);
  const financialPreviewVersion = asOptionalString(record?.financialPreviewVersion);
  const rawFinancialBlockedReasons = Array.isArray(record?.financialBlockedReasons)
    ? record.financialBlockedReasons
    : undefined;
  const unresolvedCommand = normalizeUnresolvedSettlementCommand(
    record?.unresolvedCommand,
    `${entity}.unresolvedCommand`,
  );

  return {
    orderId: asString(record?.orderId),
    canSubmit: record?.canSubmit === true,
    physicalVersion: asString(record?.physicalVersion),
    blockedReasons: asArray(record?.blockedReasons, (item) => { const value = asRecord(item); return { code: asString(value?.code), message: asString(value?.message) }; }),
    commandState,
    lines: asArray(record?.lines, (item) => { const value = asRecord(item); return { orderProductId: asString(value?.orderProductId), productName: asString(value?.productName), unit: asOptionalString(value?.unit) ?? null, orderedQuantity: number(value?.orderedQuantity), deliveredQuantity: number(value?.deliveredQuantity), settlementEligibleQuantity: number(value?.settlementEligibleQuantity), recognizedQuantity: number(value?.recognizedQuantity), readyQuantity: number(value?.readyQuantity), basisTransportIds: asArray(value?.basisTransportIds, (basisTransportId) => asString(basisTransportId)) }; }),
    transports: asArray(record?.transports, (item) => { const value = asRecord(item); return { id: asString(value?.id), status: asString(value?.status), routeNumber: asString(value?.routeNumber), actualDelivery: asOptionalString(value?.actualDelivery) ?? null, cargoPresent: value?.cargoPresent === true }; }),
    basisTransports: asArray(record?.basisTransports, (item) => { const value = asRecord(item); return { transportId: asString(value?.transportId), routeNumber: asString(value?.routeNumber), status: asString(value?.status), actualDelivery: asOptionalString(value?.actualDelivery) ?? null, cargo: asArray(value?.cargo, (cargo) => { const c = asRecord(cargo); return { orderProductId: asString(c?.orderProductId), unit: asOptionalString(c?.unit) ?? null, deliveredQuantity: number(c?.deliveredQuantity), readyQuantity: number(c?.readyQuantity) }; }) }; }),
    actions: {
      submit: asRecord(record?.actions)?.submit === true,
      finalize: asRecord(record?.actions)?.finalize === true,
      ...(asRecord(record?.actions)?.finalizeWithRemainder === true
        ? { finalizeWithRemainder: true }
        : {}),
    },
    ...(financialPreviewVersion ? { financialPreviewVersion } : {}),
    ...(rawFinancialPreview ? {
      financialPreview: {
        revenue: number(rawFinancialPreview.revenue),
        totalCost: number(rawFinancialPreview.totalCost),
        margin: number(rawFinancialPreview.margin),
        marginPercent: number(rawFinancialPreview.marginPercent),
      },
    } : {}),
    ...(rawFinancialBlockedReasons ? {
      financialBlockedReasons: rawFinancialBlockedReasons.map((item) => {
        const value = asRecord(item);
        return { code: asString(value?.code), message: asString(value?.message) };
      }),
    } : {}),
    ...(unresolvedCommand ? { unresolvedCommand } : {}),
  };
}

export function normalizeOrderSettlementCommandResult(
  raw: unknown,
  entity = 'orders.settlementCommand',
): OrderSettlementCommandResultDto {
  const record = asRecord(raw);
  if (!record) reportDtoAnomaly(entity, 'root', raw);

  return {
    state: normalizeSettlementCommandResultState(record?.state, `${entity}.state`),
    outcome: normalizeSettlementCommandOutcome(record?.outcome, `${entity}.outcome`),
  };
}

export function normalizeOrderSettlementCommandSubmitResult(
  raw: unknown,
  entity = 'orders.settlementCommand.submit',
): OrderSettlementCommandSubmitResultDto {
  const result = normalizeOrderSettlementCommandResult(raw, entity);
  return {
    state: result.state === 'SUPERSEDED' ? null : result.state,
    outcome: result.outcome === 'SUPERSEDED' ? null : result.outcome,
  };
}

function normalizeSettlementCommandState(
  value: unknown,
  entity: string,
): OrderSettlementCommandState | null {
  if (value == null) return null;
  if (value === 'APPLIED' || value === 'ADMIN_REQUIRED' || value === 'SUPERSEDED') return value;
  reportDtoAnomaly(entity, 'enum', value);
  return null;
}

function normalizeSettlementCommandResultState(
  value: unknown,
  entity: string,
): OrderSettlementCommandState | null {
  return normalizeSettlementCommandState(value, entity);
}

function normalizeSettlementCommandOutcome(
  value: unknown,
  entity: string,
): OrderSettlementCommandOutcome | null {
  if (value == null) return null;
  if (value === 'APPLIED' || value === 'ADMIN_REQUIRED' || value === 'DUPLICATE' || value === 'SUPERSEDED') return value;
  reportDtoAnomaly(entity, 'enum', value);
  return null;
}

function normalizeUnresolvedSettlementCommand(
  raw: unknown,
  entity: string,
): UnresolvedOrderSettlementCommandDto | undefined {
  const record = asRecord(raw);
  if (!record) return undefined;

  const id = asOptionalString(record.id);
  const physicalVersion = asOptionalString(record.physicalVersion);
  const state = normalizeSettlementCommandState(record.state, `${entity}.state`);

  if (!id || !physicalVersion || state !== 'ADMIN_REQUIRED') {
    reportDtoAnomaly(entity, 'shape', raw);
    return undefined;
  }

  return {
    id,
    state,
    physicalVersion,
    internalReasonCode: asOptionalString(record.internalReasonCode) ?? null,
    ...(asOptionalString(record.recognitionMonth) ? { recognitionMonth: asOptionalString(record.recognitionMonth)! } : {}),
  };
}

export function normalizeOrderSettlement(raw: unknown, entity = 'orders.settlement'): OrderSettlementDto {
  const record = asRecord(raw);
  const planned = asRecord(record?.planned);
  const actual = asRecord(record?.actual);
  const settled = asRecord(record?.settled);
  const settlementStatus = record?.settlementStatus === 'SETTLED'
    || record?.settlementStatus === 'PARTIALLY_SETTLED'
    || record?.settlementStatus === 'OPEN'
    ? record.settlementStatus
    : 'OPEN';

  if (!record) {
    reportDtoAnomaly(entity, 'root', raw);
  }

  return {
    requiresUpdate: record?.requiresUpdate === true,
    invoiceBased: record?.invoiceBased === true,
    costStatus: record?.costStatus === 'PENDING' ? 'PENDING' : 'KNOWN',
    planned: {
      revenue: planned?.revenue == null ? undefined : asNumber(planned.revenue),
      totalCost: planned?.totalCost == null ? undefined : asNumber(planned.totalCost),
      margin: planned?.margin == null ? undefined : asNumber(planned.margin),
      marginPercent: planned?.marginPercent == null ? undefined : asNumber(planned.marginPercent),
    },
    actual: {
      revenue: asNumber(actual?.revenue),
      totalCost: financeNumber(actual?.totalCost),
      margin: financeNumber(actual?.margin),
      marginPercent: financeNumber(actual?.marginPercent),
    },
    settlementStatus,
    settled: settled ? {
      revenue: asNumber(settled.revenue),
      totalCost: financeNumber(settled.totalCost),
      margin: financeNumber(settled.margin),
      marginPercent: financeNumber(settled.marginPercent),
      settledAt: asOptionalString(settled.settledAt),
    } : null,
    entries: asArray(record?.entries, (item, index) => normalizeOrderSettlementEntry(item, `${entity}.entries[${index}]`)),
  };
}

function normalizeOrderSettlementEntry(raw: unknown, entity: string): OrderSettlementEntry {
  const record = asRecord(raw);
  if (!record) {
    reportDtoAnomaly(entity, 'shape', raw);
  }

  const kind = record?.kind === 'FINAL' || record?.kind === 'CORRECTION'
    ? record.kind
    : 'PARTIAL';

  return {
    id: asString(record?.id),
    orderId: asString(record?.orderId),
    kind,
    recognizedAt: asString(record?.recognizedAt),
    revenue: asNumber(record?.revenue),
    totalCost: financeNumber(record?.totalCost),
    margin: financeNumber(record?.margin),
    marginPercent: financeNumber(record?.marginPercent),
    note: asOptionalString(record?.note) ?? null,
    createdById: asOptionalString(record?.createdById) ?? null,
    createdByName: asOptionalString(record?.createdByName) ?? null,
    createdByEmail: asOptionalString(record?.createdByEmail) ?? null,
    createdAt: asOptionalString(record?.createdAt),
    correctionOfId: asOptionalString(record?.correctionOfId) ?? null,
  };
}
